import React from 'react';
import { ShieldCheck, Globe2, Ship, Anchor,  Search, CheckCircle } from 'lucide-react';

const CommodityAbout = ({ company }) => {
  return (
    <div className="animate-in fade-in duration-1000 bg-white">
      {/* 1. TECHNICAL HERO SECTION */}
      <section className="relative pt-24 pb-32 overflow-hidden border-b border-blue-50">
        {/* Engineering-style Blueprint Grid Background */}
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none" 
             style={{ backgroundImage: `linear-gradient(#0369a1 1px, transparent 1px), linear-gradient(90deg, #0369a1 1px, transparent 1px)`, backgroundSize: '40px 40px' }}></div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <div className="lg:w-1/2">
              <div className="inline-block px-4 py-1.5 mb-6 rounded-md bg-blue-600 text-white text-[10px] font-bold tracking-[0.3em] uppercase">
                Corporate Profile
              </div>
              <h1 className="text-5xl lg:text-7xl font-black text-slate-900 mb-8 leading-[0.9] tracking-tighter">
                Bridging <br />
                <span className="text-blue-600">Global</span> Markets.
              </h1>
              <p className="text-xl text-slate-600 leading-relaxed border-l-4 border-blue-600 pl-6 italic">
                {company.description || "Leading the export of authentic Sri Lankan commodities with a focus on transparency, quality, and direct-from-source procurement."}
              </p>
            </div>
            
            <div className="lg:w-1/2 grid grid-cols-2 gap-4">
              <div className="bg-blue-50 p-8 rounded-2xl border border-blue-100">
                <div className="text-4xl font-black text-blue-700 mb-2">24h</div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-widest">Supply Response</div>
              </div>
              <div className="bg-slate-900 p-8 rounded-2xl">
                <div className="text-4xl font-black text-white mb-2">100%</div>
                <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">Quality Audit</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. THE PROCESS FLOW (Informational Breakdown) */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <h2 className="text-3xl font-black text-slate-900 mb-4">The Supply Chain Blueprint</h2>
            <p className="text-slate-500">How we manage the journey from Sri Lankan farms to international ports.</p>
          </div>

          <div className="grid md:grid-cols-4 gap-4">
            {[
              { icon: <Search />, step: "01", title: "Direct Sourcing", desc: "Identification of premium grade harvests through direct plantation audits." },
              { icon: <ShieldCheck />, step: "02", title: "Refining", desc: "In-house cleaning, sorting, and lab testing for moisture and purity." },
              { icon: <Anchor />, step: "03", title: "Compliance", desc: "Standardization for ISO and international phytosanitary certificates." },
              { icon: <Ship />, step: "04", title: "Dispatch", desc: "Consolidated freight management with real-time global tracking." }
            ].map((item, i) => (
              <div key={i} className="bg-white p-8 border border-slate-200 hover:border-blue-500 transition-colors group">
                <div className="text-blue-600 mb-6 group-hover:scale-110 transition-transform">{item.icon}</div>
                <div className="text-3xl font-black text-slate-200 mb-2">{item.step}</div>
                <h4 className="font-bold text-slate-800 mb-3">{item.title}</h4>
                <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. CORE PILLARS (Detailed Grid) */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-4xl font-black text-slate-900 mb-8 tracking-tight">Our Standards of Operation</h2>
            <div className="space-y-8">
              {[
                { title: "Ethical Trade Partnership", text: "We ensure fair-market value for Sri Lankan farmers, fostering sustainable agriculture and long-term supply stability." },
                { title: "Precision Logistics", text: "Utilizing modern air and sea freight optimization to reduce lead times and preserve the chemical profile of spices and oils." },
                { title: "Transparency & Traceability", text: "Every batch of commodities is assigned a batch ID, allowing clients to trace the product back to its specific harvest zone." }
              ].map((pill, idx) => (
                <div key={idx} className="flex gap-4">
                  <CheckCircle className="text-blue-600 shrink-0 mt-1" size={24} />
                  
                  <div>
                    <h5 className="font-bold text-slate-800 text-lg mb-1">{pill.title}</h5>
                    <p className="text-slate-500 leading-relaxed">{pill.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="relative">
            <div className="aspect-square bg-blue-600 rounded-3xl overflow-hidden relative group">
               {/* Informational Overlay */}
               <div className="absolute inset-0 p-12 flex flex-col justify-end bg-gradient-to-t from-black/80 via-black/20 to-transparent">
                  <div className="text-blue-400 font-mono text-xs tracking-widest mb-2">ESTABLISHED NETWORK</div>
                  <h3 className="text-3xl font-bold text-white leading-tight">Delivering the Essence of Sri Lanka to 40+ Nations.</h3>
               </div>
               <Globe2 className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white/10 w-64 h-64 rotate-12" />
            </div>
            {/* Abstract Decorative Element */}
            <div className="absolute -bottom-6 -right-6 w-32 h-32 border-[12px] border-slate-900 rounded-full hidden lg:block"></div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CommodityAbout;